import { useCallback } from "react"
// @ts-ignore - Will be available after GraphQL codegen
import {
  GetAlertDocument,
  GetAlertQuery,
  GetAlertQueryVariables,
  UpdateAlertStatusDocument,
  UpdateAlertStatusMutation,
  UpdateAlertStatusMutationVariables,
  AddAlertNoteDocument,
  AddAlertNoteMutation,
  AddAlertNoteMutationVariables,
  ArchiveAlertDocument,
  ArchiveAlertMutation,
  ArchiveAlertMutationVariables,
} from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint, useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

/**
 * Hook for managing a single alert
 *
 * Provides query for alert details and mutations for triage workflow.
 */
export function useAlert(alertId: string) {
  // Query for alert details
  const { data, loading, error, refetch } = useQueryWithEndpoint<GetAlertQuery, GetAlertQueryVariables>(
    GetAlertDocument,
    {
      variables: { id: alertId },
      fetchPolicy: "cache-and-network",
      skip: !alertId,
    },
    "primary",
  )

  // Mutation for updating alert status
  const [updateStatusMutation, { loading: updatingStatus }] = useMutationWithEndpoint<
    UpdateAlertStatusMutation,
    UpdateAlertStatusMutationVariables
  >(UpdateAlertStatusDocument, {}, "primary")

  // Mutation for adding notes
  const [addNoteMutation, { loading: addingNote }] = useMutationWithEndpoint<
    AddAlertNoteMutation,
    AddAlertNoteMutationVariables
  >(AddAlertNoteDocument, {}, "primary")

  // Mutation for archiving alerts
  const [archiveMutation, { loading: archiving }] = useMutationWithEndpoint<
    ArchiveAlertMutation,
    ArchiveAlertMutationVariables
  >(ArchiveAlertDocument, {}, "primary")

  // Update alert status (attend/complete)
  const updateStatus = useCallback(
    async (status: "new" | "acknowledged" | "attended" | "completed", note?: string) => {
      try {
        await updateStatusMutation({
          variables: {
            input: {
              id: alertId,
              status,
              note,
            },
          },
          refetchQueries: [
            { query: GetAlertDocument, variables: { id: alertId } },
            "GetAlerts", // Refetch alerts list
          ],
        })
        return { success: true }
      } catch (error) {
        console.error("Failed to update alert status:", error)
        return { success: false, error }
      }
    },
    [alertId, updateStatusMutation],
  )

  // Add note to alert
  const addNote = useCallback(
    async (note: string) => {
      try {
        await addNoteMutation({
          variables: {
            input: {
              id: alertId,
              note,
            },
          },
          refetchQueries: [{ query: GetAlertDocument, variables: { id: alertId } }],
        })
        return { success: true }
      } catch (error) {
        console.error("Failed to add note:", error)
        return { success: false, error }
      }
    },
    [alertId, addNoteMutation],
  )

  // Archive alert
  const archive = useCallback(async () => {
    try {
      await archiveMutation({
        variables: { id: alertId },
        refetchQueries: [
          "GetAlerts", // Refetch alerts list
        ],
      })
      return { success: true }
    } catch (error) {
      console.error("Failed to archive alert:", error)
      return { success: false, error }
    }
  }, [alertId, archiveMutation])

  return {
    alert: data?.getAlert,
    loading,
    error,
    refetch,
    updateStatus,
    addNote,
    archive,
    updatingStatus,
    addingNote,
    archiving,
  }
}
